import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ProfileData {
  full_name?: string;
  job_title?: string;
  phone?: string;
  bio?: string;
  avatar_url?: string;
  avatar_source?: 'upload' | 'social' | 'ai_generated';
  social_profile_url?: string;
  character_type?: 'professional' | 'friendly' | 'authoritative';
  timezone?: string;
}

export const useProfileSetup = () => {
  const [profileCompleted, setProfileCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileData | null>(null);

  const checkProfileCompleted = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      setProfile(data as any);
      setProfileCompleted((data as any)?.profile_completed || false);
      return (data as any)?.profile_completed || false;
    } catch (error: any) {
      console.error('Error checking profile:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: ProfileData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      setProfile(prev => ({ ...prev, ...updates }));
      toast.success('Profile updated');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
      throw error;
    }
  };

  const uploadAvatar = async (
    file: File,
    source: 'upload' | 'social' | 'ai_generated'
  ): Promise<string> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      await updateProfile({ avatar_url: publicUrl, avatar_source: source });

      return publicUrl;
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      toast.error('Failed to upload avatar');
      throw error;
    }
  };

  const completeProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update({ profile_completed: true } as any)
        .eq('id', user.id);

      if (error) throw error;

      setProfileCompleted(true);
      toast.success('Profile setup complete!');
    } catch (error: any) {
      console.error('Error completing profile:', error);
      toast.error('Failed to complete profile');
      throw error;
    }
  };

  useEffect(() => {
    checkProfileCompleted();
  }, []);

  return {
    profileCompleted,
    loading,
    profile,
    updateProfile,
    uploadAvatar,
    completeProfile,
    refetch: checkProfileCompleted,
  };
};
